import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Modal } from '../../../design-system/Modal';
import { Input, Textarea } from '../../../design-system/Input';
import { Select } from '../../../design-system/Dropdown';
import {
  Play,
  CheckCircle2,
  Plus,
  ArrowUpRight,
  TrendingDown,
  Flame,
  CheckCheck
} from 'lucide-react';

export const SprintsView = () => {
  const {
    tasks = [],
    setSelectedTask,
    setIsQuickAddOpen,
    activeSprint,
    sprints = [],
    startSprint,
    completeSprint,
    moveTaskToSprint,
    addTask
  } = useWorkspace();

  const [isStartSprintModalOpen, setIsStartSprintModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [sprintName, setSprintName] = useState(`Sprint ${sprints.length + 1}`);
  const [duration, setDuration] = useState('2 weeks');
  const [goal, setGoal] = useState('Deliver core features and test sprint deliverables.');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(
    new Date(Date.now() + 86400000 * 14).toISOString().split('T')[0]
  );
  const [selectedBacklogTaskIds, setSelectedBacklogTaskIds] = useState([]);
  const [quickBacklogTitle, setQuickBacklogTitle] = useState('');

  // Sprints & Backlog segmentation
  const sprintTasks = activeSprint
    ? tasks.filter((t) => t.sprintId === activeSprint.id || (t.status !== 'backlog' && !t.sprintId))
    : [];
  const backlogTasks = tasks.filter((t) => t.status === 'backlog' || (!t.sprintId && t.status === 'todo'));

  // Calculate story points
  const parseEstimate = (est) => {
    if (!est) return 3;
    const match = String(est).match(/\d+/);
    return match ? parseInt(match[0], 10) : 3;
  };

  const totalSprintPoints = sprintTasks.reduce((acc, t) => acc + parseEstimate(t.estimate), 0);
  const completedSprintPoints = sprintTasks
    .filter((t) => t.status === 'done')
    .reduce((acc, t) => acc + parseEstimate(t.estimate), 0);
  const sprintProgress = totalSprintPoints > 0 ? Math.round((completedSprintPoints / totalSprintPoints) * 100) : 0;

  const incompleteCount = sprintTasks.filter((t) => t.status !== 'done').length;

  const durationOptions = [
    { value: '1 week', label: '1 Week' },
    { value: '2 weeks', label: '2 Weeks (Standard)' },
    { value: '3 weeks', label: '3 Weeks' },
    { value: '4 weeks', label: '4 Weeks' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const handleDurationChange = (val) => {
    setDuration(val);
    const days = val === '1 week' ? 7 : val === '3 weeks' ? 21 : val === '4 weeks' ? 28 : 14;
    setEndDate(new Date(Date.now() + 86400000 * days).toISOString().split('T')[0]);
  };

  const handleOpenStartSprint = () => {
    setSprintName(`Sprint ${sprints.length + 1}: Sprint Delivery`);
    setSelectedBacklogTaskIds(backlogTasks.map((t) => t.id));
    setIsStartSprintModalOpen(true);
  };

  const handleStartSprintSubmit = (e) => {
    e?.preventDefault();
    startSprint({
      name: sprintName,
      duration,
      goal,
      startDate,
      endDate,
      taskIds: selectedBacklogTaskIds
    });
    setIsStartSprintModalOpen(false);
  };

  const handleConfirmCompleteSprint = () => {
    if (activeSprint) {
      completeSprint(activeSprint.id);
      setIsCompleteModalOpen(false);
    }
  };

  const handleQuickAddBacklog = (e) => {
    e?.preventDefault();
    if (!quickBacklogTitle.trim()) return;
    addTask({
      title: quickBacklogTitle.trim(),
      status: 'backlog',
      priority: 'medium',
      estimate: '3 pts',
      sprintId: null
    });
    setQuickBacklogTitle('');
  };

  const toggleBacklogSelection = (taskId) => {
    setSelectedBacklogTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  // Mock Burndown Curve Data (Ideal vs Actual)
  const burndownDays = [
    { day: 'Day 1', ideal: 24, actual: 24 },
    { day: 'Day 3', ideal: 20, actual: 22 },
    { day: 'Day 6', ideal: 15, actual: 16 },
    { day: 'Day 9', ideal: 10, actual: 11 },
    { day: 'Day 12', ideal: 5, actual: 6 },
    { day: 'Day 14', ideal: 0, actual: Math.max(0, totalSprintPoints - completedSprintPoints) }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Top Header Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Sprint Planning & Velocity</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Plan agile iterations, track burndown velocity, and groom backlog items
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            variant="primary"
            size="md"
            icon={<Play size={16} />}
            onClick={handleOpenStartSprint}
          >
            {activeSprint ? 'Plan Next Sprint' : 'Start Sprint'}
          </Button>
        </div>
      </div>

      {/* Active Sprint Section */}
      {activeSprint ? (
        <Card padding="24px">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Badge variant="teal" size="sm">Active Sprint</Badge>
                <h3 style={{ fontSize: '18px', fontWeight: '800' }}>{activeSprint.name}</h3>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                {activeSprint.startDate} – {activeSprint.endDate} • {activeSprint.duration || '2 weeks'}
              </p>
              {activeSprint.goal && (
                <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginTop: '6px', fontWeight: '500' }}>
                  <strong>Sprint Goal:</strong> {activeSprint.goal}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                variant="secondary"
                size="sm"
                icon={<CheckCircle2 size={15} color="var(--color-success)" />}
                onClick={() => setIsCompleteModalOpen(true)}
              >
                Complete Sprint
              </Button>
            </div>
          </div>

          {/* Velocity Progress Bar & Burn Stats */}
          <div
            style={{
              margin: '16px 0 20px',
              backgroundColor: 'var(--surface-recessed)',
              padding: '16px 20px',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Flame size={16} color="var(--accent-teal)" />
                <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                  Burndown Velocity: {completedSprintPoints} / {totalSprintPoints} Points Completed
                </span>
              </div>
              <span style={{ fontWeight: '800', color: 'var(--accent-teal)', fontSize: '14px' }}>{sprintProgress}%</span>
            </div>

            <div
              style={{
                height: '8px',
                borderRadius: '999px',
                backgroundColor: 'var(--surface-card)',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${sprintProgress}%`,
                  backgroundColor: 'var(--accent-teal)',
                  borderRadius: '999px',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>

            {/* Visual Burndown Trajectory Mini Graph */}
            <div style={{ paddingTop: '8px', borderTop: '1px solid var(--border-divider)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '8px' }}>
                <TrendingDown size={14} color="var(--accent-teal)" />
                <span>BURNDOWN TRAJECTORY (IDEAL VS ACTUAL)</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', textAlign: 'center' }}>
                {burndownDays.map((b) => (
                  <div key={b.day} style={{ backgroundColor: 'var(--surface-card)', padding: '6px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{b.day}</div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: 'var(--accent-teal)' }}>{b.actual} pts</div>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Ideal: {b.ideal}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sprint Tasks List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sprintTasks.map((t) => (
              <div
                key={t.id}
                onClick={() => setSelectedTask(t)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>{t.id}</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Badge variant={t.status === 'done' ? 'success' : t.status === 'in_progress' ? 'teal' : 'neutral'} size="xs">
                    {t.status.replace('_', ' ')}
                  </Badge>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                    {t.estimate || '3 pts'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card padding="32px">
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--surface-recessed)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Play size={24} color="var(--accent-teal)" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '800' }}>No Active Sprint</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '420px' }}>
              You do not have a sprint running currently. Select tasks from the backlog below and start a sprint.
            </p>
            <Button
              variant="primary"
              size="md"
              icon={<Play size={16} />}
              onClick={handleOpenStartSprint}
            >
              Start a New Sprint
            </Button>
          </div>
        </Card>
      )}

      {/* Product Backlog Section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '700' }}>Product Backlog ({backlogTasks.length})</h3>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Candidate items ready for upcoming sprints
            </span>
          </div>

          <Button
            variant="secondary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            New Task
          </Button>
        </div>

        {/* Inline Quick Add into Backlog */}
        <form onSubmit={handleQuickAddBacklog} style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <Input
            placeholder="Quick add item to backlog and press Enter..."
            value={quickBacklogTitle}
            onChange={(e) => setQuickBacklogTitle(e.target.value)}
          />
          <Button variant="secondary" size="md" icon={<Plus size={16} />} type="submit">
            Add
          </Button>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {backlogTasks.length === 0 ? (
            <div style={{ padding: '28px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              Backlog is clear.
            </div>
          ) : (
            backlogTasks.map((t) => (
              <div
                key={t.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div
                  onClick={() => setSelectedTask(t)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}
                >
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>{t.id}</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                    {t.estimate || '3 pts'}
                  </span>
                  {activeSprint && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<ArrowUpRight size={13} />}
                      onClick={() => moveTaskToSprint(t.id, activeSprint.id)}
                    >
                      Move to Active Sprint
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* START SPRINT MODAL */}
      <Modal
        isOpen={isStartSprintModalOpen}
        onClose={() => setIsStartSprintModalOpen(false)}
        title="Start Sprint"
        subtitle="Configure sprint parameters and commit backlog tasks"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsStartSprintModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleStartSprintSubmit} icon={<Play size={15} />}>
              Start Sprint ({selectedBacklogTaskIds.length} tasks committed)
            </Button>
          </>
        }
      >
        <form onSubmit={handleStartSprintSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            label="Sprint Name *"
            value={sprintName}
            onChange={(e) => setSprintName(e.target.value)}
            required
            autoFocus
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Select
              label="Duration"
              options={durationOptions}
              value={duration}
              onChange={handleDurationChange}
            />

            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <Input
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />

          <Textarea
            label="Sprint Goal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={2}
            placeholder="What is the key target or milestone for this sprint?"
          />

          {/* Select Backlog tasks to commit */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Select Tasks to Commit ({selectedBacklogTaskIds.length} selected)
            </div>

            <div
              style={{
                maxHeight: '160px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                padding: '6px',
                backgroundColor: 'var(--surface-recessed)',
                borderRadius: 'var(--radius-md)'
              }}
            >
              {backlogTasks.length === 0 ? (
                <div style={{ padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  No backlog tasks available. New tasks can be added directly during the sprint.
                </div>
              ) : (
                backlogTasks.map((t) => {
                  const isChecked = selectedBacklogTaskIds.includes(t.id);
                  return (
                    <div
                      key={t.id}
                      onClick={() => toggleBacklogSelection(t.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        backgroundColor: isChecked ? 'rgba(35, 190, 179, 0.12)' : 'var(--surface-card)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>
                          {t.id}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{t.title}</span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {t.estimate || '3 pts'}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </form>
      </Modal>

      {/* COMPLETE SPRINT MODAL */}
      {isCompleteModalOpen && (
        <Modal
          isOpen={isCompleteModalOpen}
          onClose={() => setIsCompleteModalOpen(false)}
          title={`Complete ${activeSprint?.name || 'Sprint'}`}
          subtitle="Review completed deliverables and handle rollover tasks"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsCompleteModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmCompleteSprint} icon={<CheckCheck size={15} />}>
                Complete & Rollover Incomplete Tasks
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ padding: '16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                Sprint Summary
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                • <strong>{sprintTasks.filter((t) => t.status === 'done').length}</strong> tasks completed ({completedSprintPoints} pts)
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                • <strong>{incompleteCount}</strong> incomplete tasks will be safely rolled over to the product backlog.
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
